// Apporteurs de partenaires (D-096) : chaque adulte du club présente des entreprises au club.
// Ici, sans base ni écran : le lien et le message tout prêt (vouvoiement : c'est pour l'entreprise),
// la règle de la carte d'accueil, les chiffres de la page partenaires, les libellés des pistes.
// Jamais de commission en argent : des missions Ambassade (D-100 : 1 à la piste, 3 à la signature), un cadeau du club.
import { ofClub } from './derogation';
import { euros } from './licences';

export const REF_MISSIONS = { piste: 1, signed: 3 } as const;
/** Le premier apporteur garde l'entreprise pendant 6 mois ; une piste « Nouveau » est rappelée au bureau à 7 jours. */
export const CLAIM_MONTHS = 6;
export const REMIND_DAYS = 7;

export type LeadStatus = 'nouveau' | 'contacte' | 'signe' | 'sans_suite';
export type LeadSource = 'page' | 'lien' | 'contact';
export const LEAD_STATUSES: LeadStatus[] = ['nouveau', 'contacte', 'signe', 'sans_suite'];
/** Le bureau : les quatre états, visibles d'un coup. */
export const LEAD_LABEL: Record<LeadStatus, string> = { nouveau: 'Nouveau', contacte: 'Contacté', signe: 'Signé', sans_suite: 'Sans suite' };
/** L'apporteur : où en est sa piste. */
export const MY_LEAD_CHIP: Record<LeadStatus, { text: string; cls: string }> = {
  nouveau: { text: 'au bureau', cls: 'chip-mute' },
  contacte: { text: 'en discussion', cls: 'chip-warn' },
  signe: { text: 'partenaire ✓', cls: 'chip-ok' },
  sans_suite: { text: 'sans suite', cls: 'chip-mute' },
};

/** « maman d'une joueuse » → « maman d’une joueuse » (apostrophe typographique, comme le reste de l'app). */
export const typo = (s: string): string => s.replace(/'/g, '’');

/** Le lien de l'apporteur : la page partenaires du club, « Recommandé par … ». */
export const refUrl = (origin: string, token: string): string => `${origin.replace(/\/+$/, '')}/partenaires/${token}`;

/** Le message à envoyer à l'entreprise (vouvoiement), modifiable avant l'envoi. */
export function refMessage(o: { clubName: string; intro: string | null; members: number; url: string }): string {
  const club = ofClub(o.clubName);
  const me = o.intro ? `je suis ${typo(o.intro)} ${club}` : `je fais partie ${club}`;
  const who = o.members > 0 ? `ses ${o.members} licenciés` : 'ses licenciés';
  return `Bonjour, ${me}. Le club cherche des partenaires pour la saison : visibilité au gymnase et sur l’app du club, `
    + `avantages pour ${who}. Voici ce qu’il propose :\n${o.url}`;
}

const dayMs = 86_400_000;
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / dayMs);

/**
 * La carte d'accueil « Tu connais une entreprise ? » (règle D-096), pour les comptes adultes :
 *  - jamais tant que le bureau n'a saisi aucune formule (la page de l'entreprise serait vide) ;
 *  - la première semaine de chaque mois (du 1er au 7), et la semaine qui suit la première formule (lancement) ;
 *  - « Plus tard », ou une entreprise proposée, la cachent jusqu'à la fenêtre suivante.
 * Renvoie le premier jour de la fenêtre en cours, sinon null.
 */
export function referralWindow(today: string, packsSince: string | null): string | null {
  if (!packsSince) return null;
  const launch = packsSince.slice(0, 10);
  if (today >= launch && daysBetween(launch, today) < 7) return launch;
  if (Number(today.slice(8, 10)) <= 7) return `${today.slice(0, 8)}01`;
  return null;
}

export function showReferralCard(today: string, packsSince: string | null, snoozedOn: string | null): boolean {
  const start = referralWindow(today, packsSince);
  return !!start && !(snoozedOn && snoozedOn >= start);
}

// -----------------------------------------------------------------------------
// La page partenaires du club (l'entreprise)
// -----------------------------------------------------------------------------
export interface OfferNumbers { members: number; youth: number; teams: number; home_matches: number; visits: number | null }

/** Les visites ne s'affichent que mesurées et parlantes (100 par mois au moins). */
export const visitsShown = (n: number | null | undefined): n is number => typeof n === 'number' && n >= 100;
const fr = (n: number) => n.toLocaleString('fr-FR');
const roundVisits = (n: number) => (n >= 1000 ? Math.floor(n / 100) * 100 : Math.floor(n / 10) * 10);

/** Les chiffres du club, calculés tout seuls ; un chiffre nul ne s'affiche pas. */
export function offerNumbers(n: OfferNumbers): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  if (n.members > 0) {
    const pct = Math.round((n.youth * 100) / n.members);
    out.push({ value: fr(n.members), label: pct >= 1 ? `licenciés, dont ${pct} % de jeunes` : 'licenciés' });
  }
  if (n.teams > 0) out.push({ value: fr(n.teams), label: n.teams > 1 ? 'équipes engagées' : 'équipe engagée' });
  if (n.home_matches > 0) out.push({ value: fr(n.home_matches), label: n.home_matches > 1 ? 'matchs à domicile cette saison' : 'match à domicile cette saison' });
  if (visitsShown(n.visits)) out.push({ value: fr(roundVisits(n.visits)), label: 'visites par mois de la page du club' });
  return out;
}

/** « Associez votre nom à 182 licenciés, leurs familles et tout un gymnase, chaque week-end. » */
export const offerPitch = (members: number): string => (members > 0
  ? `Associez votre nom à ${fr(members)} licenciés, leurs familles et tout un gymnase, chaque week-end.`
  : 'Associez votre nom au club, à ses familles et à tout un gymnase, chaque week-end.');

/** Prix d'une formule : « 150 € », ou « Sur devis ». */
export const packPrice = (cents: number | null | undefined): string => (cents ? euros(cents) : 'Sur devis');

// -----------------------------------------------------------------------------
// Les pistes
// -----------------------------------------------------------------------------
/** Le bureau : d'où vient la piste (« via le lien du club : Paul Martin veut être rappelé »). */
export function leadOrigin(l: { source: LeadSource; contact_name: string | null; relation: string | null }): string {
  const who = l.contact_name?.trim() || '';
  if (l.source === 'contact') {
    return `contact donné${who ? ` : ${who}` : ''}${l.relation ? ` (${l.relation})` : ''}, d’accord pour être contactée`;
  }
  if (l.source === 'lien') return `via le lien du club${who ? ` : ${who} veut être rappelé` : ''}`;
  return `via la page publique${who ? ` : ${who}` : ''}`;
}

export interface MyLeadLike {
  company: string; contact_name: string | null; source: LeadSource; status: LeadStatus | null; mine: boolean;
  dup: 'partenaire' | 'piste' | null; pack: string | null; points: number; gift: string | null;
}

/** L'apporteur : la ligne sous le nom de l'entreprise (« Paul Martin a demandé à être rappelé · une mission Ambassade »). */
export function myLeadLine(l: MyLeadLike): string {
  if (!l.mine) return l.dup === 'partenaire' ? 'Déjà partenaire du club' : 'Déjà en piste au club : le bureau s’en occupe';
  const pts = l.dup ? '' : ' · une mission Ambassade';
  if (l.status === 'signe') {
    const gains = [l.dup ? '' : `${REF_MISSIONS.piste + REF_MISSIONS.signed} missions Ambassade`, l.gift ?? ''].filter(Boolean).join(', ');
    return `${l.pack ? `Formule ${l.pack} · ` : ''}${gains || 'Merci !'}`;
  }
  if (l.status === 'contacte') return `Le bureau est en contact${pts}`;
  if (l.status === 'sans_suite') return `Pas de suite pour le moment${pts}`;
  if (l.source === 'lien') return `${l.contact_name?.trim() || 'L’entreprise'} a demandé à être rappelé${pts}`;
  return `Contact donné au bureau${pts}`;
}

/** « 3 pistes apportées cette saison par les familles, dont 1 signée. » */
export function seasonLine(leads: number, signed: number, vous = false): string {
  if (!leads) return `Personne encore cette saison : à ${vous ? 'vous' : 'toi'} d’ouvrir le score.`;
  return `${leads} ${leads > 1 ? 'pistes apportées' : 'piste apportée'} cette saison par les membres du club${signed ? `, dont ${signed} ${signed > 1 ? 'signées' : 'signée'}` : ''}.`;
}
